import { HTTPServiceClient } from '@/packages/http/service';
import * as t from './endpoints';
import { SUBMISSION_REQUEST_INCLUDES } from './service-helper';

import {
  OrgSubmissionsRequest,
  PaginatedResult,
  OrganisationClientRequest,
  AuthorizationLetterResponse,
  DownloadSubmissionRequestsResponse,
  FetchSubmissionRequestsUrlResponse,
  SchemeOrgsRequest,
  SchemeOrgsPaginatedResult,
  AutomatchTemplatesRequest,
  ExtractTemplateContextResponse,
  AutomatchAttempts,
  AutomatchAttempt
} from './types';
import { PensionScheme } from '../pensionScheme/types';
import SubmissionRequest from '@/models/SubmissionRequest';
import Utils from '@/utils/Utils';
/**
 * --------------------------------------------------
 *	Dashboard HTTP Serice
 * --------------------------------------------------
 **/
export const AMDashboardService = {
  getSubmissionRequests(
    requestParams: OrgSubmissionsRequest,
    includes: string = SUBMISSION_REQUEST_INCLUDES
  ): Promise<PaginatedResult> {
    return HTTPServiceClient.get(
      t.GET_SUBMISSION_REQUEST(requestParams, includes)
    ).then(({ data }) => data);
  },

  async getOrganizationClients(
    params: OrganisationClientRequest
  ): Promise<PensionScheme[]> {
    const { data } = await HTTPServiceClient.get(
      t.GET_ORGANIZATION_CLIENTS(params.orgId, params.page, params.limit)
    );
    return data.data;
  },

  async getSchemeOrgs(
    params: SchemeOrgsRequest
  ): Promise<SchemeOrgsPaginatedResult> {
    const { data } = await HTTPServiceClient.get(t.GET_SCHEME_ORGS(params));
    return data;
  },

  async downloadAuthorizationLetter(
    submissionReRequestId: string
  ): Promise<AuthorizationLetterResponse> {
    const { data } = await HTTPServiceClient.get(
      t.DOWNLOAD_AUTHORIZATION_LETTER(submissionReRequestId)
    );
    return data;
  },

  async downloadSubmissionRequests(
    organizationId: string
  ): Promise<DownloadSubmissionRequestsResponse> {
    const { data } = await HTTPServiceClient.post(
      t.DOWNLOAD_SUBMISSION_REQUESTS(organizationId)
    );
    return data;
  },

  async fetchSubmissionRequestsUrl(
    jobId: string
  ): Promise<FetchSubmissionRequestsUrlResponse> {
    const { data } = await HTTPServiceClient.get(
      t.FETCH_SUBMISSION_REQUESTS_URL(jobId)
    );
    return data;
  },

  async autoMatchTemplates(
    requestParams: AutomatchTemplatesRequest,
    includes: string = SUBMISSION_REQUEST_INCLUDES
  ): Promise<{
    templateContextData: ExtractTemplateContextResponse;
    autoMatchData: Record<string, SubmissionRequest>;
  }> {
    let formData = new FormData();
    for (const file of requestParams.files) {
      formData.append('files', file, file.name);
    }
    for (const property of requestParams.propertiesToExtract) {
      formData.append('propertiesToExtract', property);
    }
    const templateContext = await HTTPServiceClient.post<
      ExtractTemplateContextResponse
    >(t.EXTRACT_TEMPLATE_CONTEXT, formData).then(({ data }) => data);

    const automatchRequest: AutomatchAttempts = {
      automatchAttemptBatchId: requestParams.automatchAttemptBatchId,
      templates: templateContext.data
        .filter(template => template.success)
        .map(template => {
          const { context } = template;
          const autoMatchAttempt: Required<AutomatchAttempt> = {
            fileName: template.fileName,
            templateContext: {}
          };
          if (context?.portfolio_identifier) {
            autoMatchAttempt.templateContext.templatePortfolioIdentifier = `${context.portfolio_identifier}`;
          }
          if (context?.client_name) {
            autoMatchAttempt.templateContext.templateClientName = `${context.client_name}`;
          }
          if (
            context?.reporting_period_start &&
            Utils.isValidDate(new Date(context.reporting_period_start)) &&
            context?.reporting_period_end &&
            Utils.isValidDate(new Date(context.reporting_period_end))
          ) {
            autoMatchAttempt.templateContext.templateReportingPeriodStart = Utils.formatDate(
              `${context.reporting_period_start}`,
              'yyyy-MM-dd'
            );
            autoMatchAttempt.templateContext.templateReportingPeriodEnd = Utils.formatDate(
              `${context.reporting_period_end}`,
              'yyyy-MM-dd'
            );
          }
          return autoMatchAttempt;
        })
    };
    if (!automatchRequest.templates.length) {
      return {
        templateContextData: templateContext,
        autoMatchData: {}
      };
    }
    const autoMatchResponse: Record<
      string,
      SubmissionRequest
    > = await HTTPServiceClient.post(
      t.AUTOMATCH_TEMPLATES(requestParams, includes),
      automatchRequest
    ).then(({ data }) => data.data);
    return {
      templateContextData: templateContext,
      autoMatchData: autoMatchResponse
    };
  }
};
